// ────────────────────────────────────────────────────────────────────
// What a file in the file room IS, and how it reads — the parts of the
// file room that are decidable without rendering anything.
//
// Extracted from IntakeScreen, which had grown past 1,600 lines with these
// buried in the middle of it. Pure functions in a .ts file are testable and
// re-usable; the same predicates gate the context menu, the preview pane and
// the import console, and they were previously reachable from none of them.
// ────────────────────────────────────────────────────────────────────

import type { IntakeFile, IntakeKind } from "@/lib/types";

export const MAX_MB = 200;
export const UPLOAD_CONCURRENCY = 3;   // files sent in parallel; bounded for big workbooks

export async function runLimited<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (i < items.length) { const idx = i++; await worker(items[idx]); }
    }),
  );
}

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
export function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const [d, t] = iso.split("T");
  return t ? `${d} ${t.slice(0, 5)}` : d;
}
export function ext(name: string): string {
  return name.includes(".") ? name.split(".").pop()!.toUpperCase() : "";
}
export function typeLabel(r: IntakeFile): string {
  const e = ext(r.filename);
  switch (r.kind) {
    case "photo":   return e ? `${e} image` : "Image";
    case "report":  return e === "PDF" ? "PDF document" : e ? `${e} document` : "Document";
    case "data":    return e ? `${e} spreadsheet` : "Spreadsheet";
    case "drawing": return e ? `${e} drawing` : "Drawing";
    default:        return e ? `${e} file` : "File";
  }
}
// Content-led delivery title: a single file names the round after itself (long
// names are clipped to 40 chars); many files collapse to a count. The staff
// "ทีมรับแทน" flow passes a provenance suffix (files arrived off-portal); a
// customer-side create omits it. The date is intentionally NOT in the title —
// it already lives on the delivery card's meta line.
export function deliveryTitle(filenames: string[], suffix = ""): string {
  const names = filenames.filter((n) => n && n.trim());
  if (names.length === 1) {
    const n = names[0].trim();
    return n.length > 40 ? `${n.slice(0, 39)}…` : n;
  }
  return `ส่ง ${names.length || filenames.length} ไฟล์${suffix}`;
}
export const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
export function openFile(r: IntakeFile): void {
  if (r.download_url) window.open(r.download_url, "_blank", "noopener");
}
export const TABULAR = /\.(xlsx|xls|csv)$/i;
export function canImportFile(f: IntakeFile): boolean {
  return !!f.asset_id && (f.kind === "data" || TABULAR.test(f.filename));
}
/** Register import CREATES the plant/area/asset hierarchy, so unlike the
 *  thickness import it must not require the file to be filed to an asset. */
export function canRegisterImportFile(f: IntakeFile): boolean {
  return f.kind === "data" || TABULAR.test(f.filename);
}

export type Location =
  | { kind: "inbox" } | { kind: "all" } | { kind: "trash" } | { kind: "type"; t: IntakeKind } | { kind: "asset"; id: number; tag: string };
export type SortKey = "name" | "date" | "type" | "size";
export const KIND_LABEL: Record<IntakeKind, string> = { photo: "Photos", report: "Reports", data: "Data", drawing: "Drawings", other: "Other" };
export const KIND_ORDER: IntakeKind[] = ["photo", "report", "data", "drawing", "other"];
export type Menu = { x: number; y: number; file: IntakeFile } | null;
export type UpStatus = "queued" | "uploading" | "done" | "failed" | "stopped";
export type UploadItem = { key: string; name: string; size: number; pct: number; status: UpStatus; file: File; note?: string };
